import { useMutation } from '@apollo/client';
import type { UpdateColourPaletteMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  namedOperations,
  UpdateColourPaletteDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

import type { UpdateColourPaletteTRPCInput } from './useUpdateColourPaletteTRPC';
import { useUpdateColourPaletteTRPC } from './useUpdateColourPaletteTRPC';

export const useUpdateColourPalette = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  // GraphQL mutation
  const [updateColourPaletteGraphQL, graphqlState] = useMutation(
    UpdateColourPaletteDocument,
    {
      refetchQueries: [namedOperations.Query.getColourPalettes],
    }
  );

  // tRPC mutation
  const trpcMutation = useUpdateColourPaletteTRPC();

  const updateColourPalette = async (
    variables: UpdateColourPaletteTRPCInput
  ): Promise<UpdateColourPaletteMutation> => {
    if (trpcEnabled) {
      return trpcMutation.updateColourPalette(variables);
    }

    const result = await updateColourPaletteGraphQL({
      variables: {
        Id: variables.Id,
        Name: variables.Name,
        Settings: variables.Settings,
      },
    });
    if (!result.data) {
      throw new Error('Failed to update colour palette');
    }

    return result.data;
  };

  // Return appropriate state based on feature flag
  if (trpcEnabled) {
    return {
      updateColourPalette,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    updateColourPalette,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
